import { useEffect, useState } from 'react';
import { supabase } from './supabase';
import type { WorkspaceObjectiveProgress } from '@shared/types/workspace-objectives';

/** An app list that can measure an objective: its statuses are what counts. */
export interface MeasurableList {
  id: string;
  title: string;
  statuses: Array<{ key: string; label: string }>;
}

/** The tenant's HubSpot portal as the measure form offers it (activity-feed). */
export interface HubSpotMeasureOptions {
  available: boolean;
  portalId: string | null;
  pipelines: Array<{ id: string; label: string; stages: Array<{ id: string; label: string }> }>;
  meetingTypes: Array<{ value: string; label: string }>;
}

interface ProgressRow {
  objective_id: string;
  source: WorkspaceObjectiveProgress['source'];
  reason: WorkspaceObjectiveProgress['reason'];
  list_title: string | null;
  status_label: string | null;
  total: number | null;
  this_week: number | null;
}

const browserTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

/** YYYY-MM-DD of a date in the browser's time zone. */
export const localIsoDate = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

export const loadObjectiveProgress = async (
  objectiveIds: string[],
): Promise<Record<string, WorkspaceObjectiveProgress>> => {
  if (objectiveIds.length === 0) return {};
  const { data, error } = await supabase.rpc('read_objective_progress_j3', {
    p_objective_ids: objectiveIds,
    p_time_zone: browserTimeZone(),
  });
  if (error) throw new Error('La progression des objectifs n’a pas pu être lue.');
  return Object.fromEntries(((data ?? []) as ProgressRow[]).map((row) => [row.objective_id, {
    objectiveId: row.objective_id,
    source: row.source,
    reason: row.reason,
    listTitle: row.list_title,
    statusLabel: row.status_label,
    total: row.total,
    thisWeek: row.this_week,
  }]));
};

/** Real pipelines, stages and meeting types of the tenant's HubSpot portal. */
export const loadHubSpotMeasureOptions = async (): Promise<HubSpotMeasureOptions> => {
  const { data, error } = await supabase.functions.invoke('activity-feed', {
    body: { action: 'objective_hubspot_options' },
  });
  if (error) throw new Error('HubSpot n’est pas accessible pour le moment.');
  return data as HubSpotMeasureOptions;
};

/** Asks the server to read HubSpot for these HubSpot-measured objectives; a
 * read younger than two minutes is reused server side. */
export const syncObjectiveHubSpot = async (objectiveIds: string[]): Promise<void> => {
  if (objectiveIds.length === 0) return;
  const { error } = await supabase.functions.invoke('activity-feed', {
    body: { action: 'sync_objective_hubspot', objectiveIds },
  });
  if (error) throw new Error('HubSpot n’a pas pu être lu.');
};

/** Counts of the measured objectives, reloaded when `refreshKey` changes and
 * when the window regains focus (statuses change in the apps). The objectives
 * measured on HubSpot (`hubspotIds`) are read from HubSpot first; meanwhile the
 * last counts show. */
export const useObjectiveProgress = (objectiveIds: string[], refreshKey: string, hubspotIds: string[] = []) => {
  const idsKey = [...objectiveIds].sort().join(',');
  const hubspotKey = [...hubspotIds].sort().join(',');
  const [progress, setProgress] = useState<Record<string, WorkspaceObjectiveProgress>>({});
  const [error, setError] = useState<string | null>(null);
  const [focusTick, setFocusTick] = useState(0);
  useEffect(() => {
    const onFocus = () => setFocusTick((tick) => tick + 1);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);
  useEffect(() => {
    let active = true;
    const ids = idsKey ? idsKey.split(',') : [];
    const read = () => loadObjectiveProgress(ids)
      .then((value) => {
        if (!active) return;
        setProgress(value);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (active) setError(cause instanceof Error ? cause.message : 'La progression des objectifs n’a pas pu être lue.');
      });
    void read();
    if (hubspotKey) {
      // A failed read leaves the server's answer (count withheld) to show.
      void syncObjectiveHubSpot(hubspotKey.split(',')).catch(() => undefined).then(() => { if (active) void read(); });
    }
    return () => { active = false; };
  }, [idsKey, hubspotKey, refreshKey, focusTick]);
  return { progress, error };
};

const parseStatuses = (value: unknown): MeasurableList['statuses'] =>
  Array.isArray(value)
    ? value.flatMap((item) => {
        if (typeof item !== 'object' || item === null) return [];
        const { key, label } = item as { key?: unknown; label?: unknown };
        return typeof key === 'string' && key
          ? [{ key, label: typeof label === 'string' && label ? label : key }]
          : [];
      })
    : [];

/** The app lists the user can see (RLS), with their statuses. */
export const loadMeasurableLists = async (): Promise<MeasurableList[]> => {
  const { data, error } = await supabase.from('app_collections').select('id, title, statuses').order('title');
  if (error) throw new Error('Les listes des applications ne sont pas accessibles pour le moment.');
  return ((data ?? []) as Array<{ id: string; title: string; statuses: unknown }>)
    .map((row) => ({ id: row.id, title: row.title, statuses: parseStatuses(row.statuses) }))
    .filter((list) => list.statuses.length > 0);
};

export interface ObjectivePace {
  remaining: number;
  /** Weeks left until the due date, today included; null without due date. */
  weeksLeft: number | null;
  /** Records per week needed to reach the target in time; null without due date. */
  perWeek: number | null;
  overdue: boolean;
}

const DAY_MS = 86_400_000;

/** Plain arithmetic on an exact count: what is left, spread over the weeks left. */
export const objectivePace = ({ total, target, dueDate, today }: {
  total: number;
  target: number;
  dueDate: string | null;
  today: Date;
}): ObjectivePace => {
  const remaining = Math.max(target - total, 0);
  const match = dueDate ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(dueDate) : null;
  if (!match) return { remaining, weeksLeft: null, perWeek: null, overdue: false };
  const due = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const start = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  const daysLeft = Math.round((due - start) / DAY_MS) + 1;
  if (daysLeft <= 0) return { remaining, weeksLeft: 0, perWeek: null, overdue: remaining > 0 };
  const weeksLeft = Math.ceil(daysLeft / 7);
  return { remaining, weeksLeft, perWeek: Math.ceil(remaining / weeksLeft), overdue: false };
};
